import React from "react";
import {
  Box,
  Heading,
  Text,
  useBreakpointValue,
  Button,
  Flex,
  Icon,
  SimpleGrid,
} from "@chakra-ui/react";
import ScrollingIcon from "./mainPage/ScrollingIcon";
import MonitorSection from "./MonitorSection";
import SEO from "../SEO";
import { darkColor, mainColor } from "./mainPage/MainPage";
import { motion } from "framer-motion";
import {
  FaMobileAlt,
  FaCalendarAlt,
  FaInfinity,
  FaEnvelope,
  FaGlobe,
  FaTags,
  FaEdit,
  FaAddressCard,
  FaStar,
  FaWpforms,
  FaBullhorn,
  FaMoneyBill,
  FaImage,
  FaPalette,
  FaChartLine,
  FaHourglassHalf,
  FaFileInvoice,
  FaUserCircle,
} from "react-icons/fa";
import VideoSection from "./VideoSection";

const listItems = [
  "Reszponzív weboldal minden eszközre",
  "Online időpontfoglaló rendszer",
  "Korlátlan szolgáltatás",
  "Korlátlan foglalás",
  "Várólista",
  "Szolgáltatás kategóriák létrehozása",
  "Számlázás (Számlázz.hu, Billingo)",
  "Előlegfizetés",
  "MyWobble ügyfélfiók",
  "Email emlékeztető",
  "Saját domain használata",
  "Blog írás, blog oldalak",
  "Szolgáltatást bemutató oldalak",
  "Ügyfélértékelések",
  "Egyedi kérdőív foglaláskor",
  "Marketing email küldés",
  "Referencia képek",
  "SEO beállítások",
  "Weboldal sablon, amit személyre szabhatsz. Hamarosan érkezik a következő sablon!",
];

const WebsiteSection: React.FC = () => {
  const sectionWidth = useBreakpointValue({
    base: "100%",
    md: "90%",
  }) as string;

  const ctaSize = useBreakpointValue({ base: "sm", md: "md" });

  const iconMap = [
    FaMobileAlt,
    FaCalendarAlt,
    FaInfinity,
    FaInfinity,
    FaHourglassHalf,
    FaTags,
    FaFileInvoice,
    FaMoneyBill,
    FaUserCircle,
    FaEnvelope,
    FaGlobe,
    FaEdit,
    FaAddressCard,
    FaStar,
    FaWpforms,
    FaBullhorn,
    FaImage,
    FaChartLine,
    FaPalette,
  ];

  return (
    <>
      <SEO
        title=" Online weboldal és időpontfoglaló rendszer - készítsd el saját
        weboldalad!"
        description="Készítsd el saját, reszponzív weboldaladat online időpontfoglaló rendszerrel, korlátlan szolgáltatásokkal, saját domainnel és egyedi sablonokkal."
        keywords="weboldal készítés, online időpontfoglaló, reszponzív design, korlátlan szolgáltatás, weboldal sablon, email emlékeztető, blog írás, ügyfélértékelések"
        ogTitle="Online weboldal és időpontfoglaló rendszer"
        ogDescription="Készítsd el saját weboldalad, ami tartalmazza az időpontfoglaló rendszert, és érd el könnyedén a vállalkozásod online jelenlétét."
        ogImage="https://wobble.hu/assets/mockup_massage.png"
      />
      <Box width="100%" p={4} textAlign="center" pb={20} bg="gray.50">
        <Heading
          as="h1"
          mb={8}
          fontSize={{ base: "3xl", md: "5xl" }}
          fontWeight="bold"
          bgGradient="linear(to-r, teal.300, blue.500)"
          bgClip="text"
          mt={20}
        >
          Online megjelenés, weboldal
        </Heading>

        <VideoSection
          title="Weboldal egyszerűen"
          subtitle="Videós útmutató"
          description="Egyszerű saját weboldal, fejlesztő nélkül, azonnal. Állítsd be és frissítsd, amikor csak szeretnéd, extra költségek nélkül."
          videoUrl="https://www.youtube.com/embed/582oLOqi3IA"
          ctaText="Kipróbálom"
          ctaLink="https://app.wobble.hu"
        />


        <Heading
          fontSize={{ base: "md", md: "xl" }}
          mt={4}
          color="gray.700"
          maxW={800}
          mb={10}
          justifySelf="center"
        >
          Készítsd el saját weboldalad, ami tartalmazza az időpontfoglaló
          rendszert is!
        </Heading>
        <Text>Pörgesd át a demo weboldalakat ⬇️</Text>
        <ScrollingIcon />
        <Box
          display="flex"
          flexDirection={{ base: "column", md: "row" }}
          justifyContent="space-between"
          width={sectionWidth}
          mx="auto"
          mb={50}
        >
          <Box
            flex="1"
            mr={{ md: 4 }}
            mb={{ base: 4, md: 0 }}
            transition="transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out"
            _hover={{ transform: "scale(1.05)" }}
          >
            <MonitorSection imageUrl="/assets/website/weboldal-kutyakozmetika-1200.WebP" />
          </Box>

          <Box
            flex="1"
            mt={{ base: 4, md: 6 }}
            p={4}
            borderRadius="lg"
            transition="transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out"
            _hover={{ transform: "scale(1.05)" }}
          >
            <MonitorSection imageUrl="/assets/website/weboldal-takaitoceg-1200.WebP" />
          </Box>
        </Box>

        <Box
          display="flex"
          flexDirection={{ base: "column", md: "row" }}
          justifyContent="space-between"
          width={sectionWidth}
          mx="auto"
          mb={50}
        >
          <Box
            flex="1"
            mr={{ md: 4 }}
            mb={{ base: 4, md: 0 }}
            transition="transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out"
            _hover={{ transform: "scale(1.05)" }}
          >
            <MonitorSection imageUrl="/assets/website/moov_studio.png" />
          </Box>
        </Box>

        <Box
          width={sectionWidth}
          mx="auto"
          p={{ base: 6, md: 8 }}
          bg="white"
          borderRadius="2xl"
          boxShadow="2xl"
          border="1px solid #e2e8f0"
          mb={8}
          maxW={900}
        >
          <Text
            as="h2"
            color={mainColor}
            fontSize={{ base: "2xl", md: "3xl" }}
            fontWeight="bold"
            textAlign="center"
            mb={8}
          >
            Mit tud a weboldal lehetőség?
          </Text>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {listItems.map((item, index) => {
              const IconComponent = iconMap[index];

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                >
                  <Flex
                    align="center"
                    borderRadius="lg"
                    p={4}
                    boxShadow="md"
                    _hover={{
                      transform: "scale(1.02)",
                      boxShadow: "lg",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Icon
                      as={IconComponent}
                      color={mainColor}
                      boxSize={6}
                      mr={4}
                      flexShrink={0}
                    />
                    <Text fontSize="md" fontWeight="medium" color="gray.700">
                      {item}
                    </Text>
                  </Flex>
                </motion.div>
              );
            })}
          </SimpleGrid>
        </Box>

        <Text
          fontSize={{ base: "md", md: "xl" }}
          mt={12}
          color={mainColor}
          maxW={800}
          mb={18}
          justifySelf="center"
          as="h3"
          minH={40}
        >
          Nincs időd a weboldallal bajlódni, de szeretnél profin jelen lenni
          online? A Wobble-t úgy terveztük, hogy bárki könnyen elindíthassa és
          frissíthesse - technikai tudás nélkül.
        </Text>

        <Heading
          fontSize={{ base: "md", md: "xl" }}
          mt={12}
          color={mainColor}
          maxW={800}
          mb={18}
          justifySelf="center"
          as="h2"
          minH={40}
        >
          Nézd meg ugyanazt a demó oldalt mindkét sablonnal, és képzeld el,
          hogyan mutatna vele a saját vállalkozásod. ⬇️
        </Heading>

        <Box
          display="flex"
          flexDirection={{ base: "column", md: "row" }}
          justifyContent="space-between"
          width={sectionWidth}
          mx="auto"
        >
          <Box
            flex="1"
            mr={{ md: 4 }}
            mb={{ base: 4, md: 0 }}
            transition="transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out"
            _hover={{ transform: "scale(1.05)" }}
          >
            <MonitorSection imageUrl="/assets/website/lumiere-szalon-default-1200.WebP" />
            <Text as="i">Alapértelmezett</Text>
          </Box>

          <Box
            flex="1"
            mt={{ base: 4, md: 6 }}
            p={4}
            borderRadius="lg"
            transition="transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out"
            _hover={{ transform: "scale(1.05)" }}
          >
            <MonitorSection imageUrl="/assets/website/lumiere-szalon-classic-1200.WebP" />
            <Text as="i">Klasszik</Text>
          </Box>
        </Box>
        <Button
          as="a"
          href="https://wobble.hu/blog/wobble-weboldal-setup-lepesrol-lepesre"
          target="_blank"
          rel="noopener noreferrer"
          _hover={{ bg: darkColor, textDecoration: "none" }}
          size={ctaSize}
          colorScheme="teal"
          mt={10}
          px={8}
          py={6}
          rounded="2xl"
          shadow="lg"
          fontWeight="bold"
        >
          Blog cikk a témában
        </Button>
      </Box>
    </>
  );
};

export default WebsiteSection;
